import { CaretDownIcon } from '@phosphor-icons/react';
import * as React from 'react';

import {
  InlinePromptComposer,
  type InlinePromptComposerHandle,
} from '@/components/ui/inspector/shared/InlinePromptComposer';
import { cn } from '@/lib/utils';

export interface FloatingPromptBarProps {
  value: string;
  onChange: (value: string) => void;
  /** Awaited. The draft clears only after this resolves, and only if untouched. */
  onSubmit: (prompt: string) => Promise<void> | void;
  /** Names what the prompt will act on — `Image`, a layer name, `3 layers`. */
  targetLabel: string;
  /** Makes the scope chip a button; the store layer supplies the menu. */
  onTargetClick?: () => void;
  placeholder?: string;
  /** A request is in flight — send becomes a spinner. */
  pending?: boolean;
  disabled?: boolean;
  /**
   * The edit-mode chip rail. Rendered on the composer's own quick-actions row,
   * beside the scope chip, so the modes are VISIBLE rather than buried.
   */
  quickActions?: React.ReactNode;
  /** Status line under the input, e.g. "Extending the background to 9:16…". */
  status?: React.ReactNode;
  className?: string;
}

/**
 * The floating prompt bar — the AI conversation living OVER the artifact rather
 * than in a right-hand chat panel. The deliberate opposite of marketer.com's
 * bound-panel chat, and of Mora's own GlobalRightDock.
 *
 * 🔴 THERE IS NO SEPARATE SHELL ANY MORE, and that is the fix, not a
 * regression. A prior pass here wrapped `InlinePromptComposer` in its own
 * `rounded-3xl border bg-background shadow-elevation-3` card plus a
 * `bg-muted` tray around it — a second border and a second fill sitting on
 * top of the composer's own `rounded-3xl border border-border/60 bg-card`.
 * Measured live, that produced TWO nested 24px-radius boxes in the same dark
 * background color plus an added `0 10px 20px rgba(0,0,0,.15)` shadow — the
 * exact "too bold, borders don't match" the founder called out, because it
 * no longer read as the same object as the composer mounted in
 * `MediaStudioDialog` (`:884-935`) or `PostBody` (`:3620-3630`), which mount
 * `InlinePromptComposer` directly with NO extra wrapper. This file now does
 * the same: it positions the composer over the canvas and supplies the
 * quick-actions row (scope chip + mode rail); every border, radius, fill and
 * shadow decision belongs to the composer alone.
 *
 * THE INPUT IS NOT OURS. The input it wraps is `InlinePromptComposer`, the
 * same component the media inspector and the Home agents composer already
 * mount. What that reuse buys, all of which this file previously
 * re-implemented and got wrong:
 *   • auto-grow to `max-h-36`. The old raw `Textarea rows={1} min-h-8` wrapped
 *     INVISIBLY — a two-line prompt scrolled inside a 32px box.
 *   • a draft that survives a failed request, and that is not clobbered if the
 *     user kept typing while the request was in flight.
 *   • the canonical `Button variant="flat" size="composer-icon"` send, so this
 *     surface stops speaking a different composer dialect from Home chat.
 *   • a `quickActions` slot, which is where the edit modes now live.
 *
 * 🔴 THE STOP BUTTON IS GONE, AND THAT IS THE FIX. The old bar rendered a
 * pulsing Stop control whenever `pending` was true, and the page never passed
 * `onStop` — so it was a live silent no-op: a control that drew itself, invited
 * a click, and did nothing. There is no abort path through
 * `/api/generate-image` to wire it to, so the honest answer is the composer's
 * own in-flight treatment (send swaps to a spinner) plus the status line, not a
 * button that lies. Re-introduce Stop only with a real AbortController behind it.
 *
 * The scope chip is NOT a violation of the composer DO-NOT-CROWD rule — that
 * rule forbids CAPABILITY chips (brand kit, Look, tone) parked on the Home
 * composer for discoverability. This chip answers "what will this prompt
 * change?" for an operation that overwrites an artifact. Removing it makes the
 * feature unsafe, which is the actual bar the Subtraction test sets.
 */
export const FloatingPromptBar = React.forwardRef<
  InlinePromptComposerHandle,
  FloatingPromptBarProps
>(function FloatingPromptBar(
  {
    value,
    onChange,
    onSubmit,
    targetLabel,
    onTargetClick,
    placeholder,
    pending = false,
    disabled = false,
    quickActions,
    status,
    className,
  },
  ref,
) {
  return (
    // WHY (measured, Hightouch): 602 / ~700 ≈ 86% of the canvas column.
    // `w-6/7` is 85.7% — the same proportion off Tailwind's own scale. This
    // wrapper carries WIDTH ONLY — no border, fill or shadow of its own — so
    // the composer's own `rounded-3xl border border-border/60 bg-card` is the
    // only surface drawn, identical to its `MediaStudioDialog`/`PostBody` mounts.
    <div className={cn('w-6/7 max-w-160', className)}>
      <InlinePromptComposer
        ref={ref}
        kind="image"
        isOperating={pending}
        value={value}
        onValueChange={onChange}
        onSubmit={onSubmit}
        placeholder={placeholder}
        className={cn(disabled && 'pointer-events-none opacity-60')}
        quickActions={
          /* Tried and REJECTED (part 1): a wrapped second row under the input.
             Measured at 252px of bar width it read worse, not better — a
             full-bleed pill above a half-empty input. The composer's own
             quick-actions row is that second row, done properly: it is INSIDE
             the composer surface, separated by its own hairline, and it scrolls
             horizontally instead of wrapping. */
          /* The edge fade is the same one `MediaStudioDialog`'s quick-actions
             rail uses, class for class — at 1024 the canvas column is 440px
             and the fifth mode chip is cut mid-word with nothing to say the
             row scrolls. An arbitrary `mask-image` is the exception the repo
             already makes here; there is no token for a gradient mask. */
          <div className="-mx-1 flex items-center gap-1.5 overflow-x-auto px-1 scrollbar-hide [mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)]">
            <ScopeChip label={targetLabel} onClick={onTargetClick} disabled={disabled} />
            {/* The scope chip and the mode chips are the same SIZE and nearly
                the same shape, and they answer completely different questions
                — "what will this change?" versus "how?". Without a rule
                between them the row reads as six peers and the scope stops
                being scope. One hairline, the same one the rails use. */}
            {quickActions ? <span aria-hidden className="h-4 w-px shrink-0 bg-border/60" /> : null}
            {quickActions}
          </div>
        }
      />

      {status ? (
        <p className="px-4 pb-1.5 pt-1 text-detail text-muted-foreground">{status}</p>
      ) : null}
    </div>
  );
});

function ScopeChip({
  label,
  onClick,
  disabled,
  className,
}: {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}): JSX.Element {
  const content = (
    <>
      <span className="truncate">{label}</span>
      {onClick ? <CaretDownIcon className="size-3 shrink-0 opacity-60" /> : null}
    </>
  );

  const shared = cn(
    'flex h-6 max-w-24 shrink-0 items-center gap-1 rounded-full bg-muted-foreground/10 px-2 text-detail text-muted-foreground',
    className,
  );

  if (!onClick) {
    return (
      <span className={shared} title={label}>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={`Change what this prompt applies to — currently ${label}`}
      className={cn(
        shared,
        'transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:pointer-events-none disabled:opacity-50',
      )}
    >
      {content}
    </button>
  );
}
